import React from "react";
import { X, Check, AlertTriangle, MessageCircle } from "lucide-react";

const CANCEL_REASONS = [
  "Change of plans / Date conflict",
  "Budget constraints",
  "Found another service provider",
  "Personal / Family emergency",
  "Other reason",
];

export const CancelBookingModal = ({
  cancelView, setCancelView, cancelStep, setCancelStep,
  cancelReason, setCancelReason, cancelCustomReason, setCancelCustomReason,
  cancelError, cancelLoading, cancelPreview,
  handleCancelPreview, handleConfirmCancel, fetchProfile, openFeedbackPopup,
}) => {
  if (!cancelView) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { if (cancelStep !== 'done') setCancelView(null); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {cancelStep === "select" && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#1E1B4B]">Cancel Booking</h3>
              <button onClick={() => setCancelView(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="bg-[#FFFAF5] rounded-xl p-3 mb-4 text-sm">
              <p className="font-semibold text-[#1E1B4B]">{cancelView.package} — {cancelView.occasion}</p>
              <p className="text-[#475569]">{new Date(cancelView.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              <p className="text-xs text-[#94a3b8]">ID: {cancelView.id}</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
              <p className="text-sm font-medium text-blue-800 mb-1">Before you cancel...</p>
              <p className="text-xs text-blue-600 mb-2">Our team can help reschedule, customize, or resolve any concerns.</p>
              <a href="https://wa.me/919930001848?text=Hi%2C%20I%20want%20to%20discuss%20my%20booking%20before%20cancelling" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#25D366] text-white text-xs font-medium hover:bg-[#1da851] transition-colors" data-testid="talk-to-consultant">
                <MessageCircle size={12} /> Talk to a Consultant
              </a>
            </div>

            <p className="text-sm font-medium text-[#1E1B4B] mb-2">Reason for cancellation</p>
            <div className="space-y-2 mb-4">
              {CANCEL_REASONS.map(r => (
                <label key={r} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer text-sm transition-all ${cancelReason === r ? 'border-[#E83E8C] bg-[#E83E8C]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="cancel-reason" value={r} checked={cancelReason === r} onChange={() => setCancelReason(r)} className="accent-[#E83E8C]" />
                  {r}
                </label>
              ))}
              {cancelReason === "Other reason" && (
                <textarea value={cancelCustomReason} onChange={(e) => setCancelCustomReason(e.target.value)} placeholder="Please tell us more..." className="w-full border rounded-lg p-2 text-sm mt-1 outline-none focus:border-[#E83E8C]" rows={2} data-testid="cancel-custom-reason" />
              )}
            </div>
            {cancelError && <p className="text-xs text-red-500 mb-2">{cancelError}</p>}
            <button onClick={handleCancelPreview} disabled={cancelLoading || !cancelReason} className="w-full py-2.5 rounded-xl bg-red-500 text-white font-semibold text-sm disabled:opacity-50 hover:bg-red-600 transition-colors" data-testid="proceed-cancel-btn">
              {cancelLoading ? "Processing..." : "Proceed to Cancel"}
            </button>
          </>
        )}

        {cancelStep === "confirm" && cancelPreview && (
          <>
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={20} className="text-amber-500" />
              <h3 className="text-lg font-bold text-[#1E1B4B]">Confirm Cancellation</h3>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 text-sm space-y-1">
              <p><span className="font-medium">Total Paid:</span> Rs {cancelPreview.refund?.amount_paid || cancelPreview.total_paid || 0}</p>
              <p><span className="font-medium">Refund Amount:</span> <span className="text-green-600 font-semibold">Rs {cancelPreview.refund?.refund_amount ?? cancelPreview.refund_amount ?? 0}</span></p>
              <p className="text-xs text-[#475569] mt-2">{cancelPreview.refund?.policy_applied || cancelPreview.policy || "Refund policy applied"}</p>
            </div>
            {cancelError && <p className="text-xs text-red-500 mb-2">{cancelError}</p>}
            <div className="flex gap-2">
              <button onClick={() => setCancelStep("select")} className="flex-1 py-2.5 rounded-xl border border-gray-300 text-[#475569] font-medium text-sm hover:bg-gray-50">Go Back</button>
              <button onClick={handleConfirmCancel} disabled={cancelLoading} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-sm disabled:opacity-50 hover:bg-red-600" data-testid="confirm-cancel-btn">
                {cancelLoading ? "Cancelling..." : "Yes, Cancel"}
              </button>
            </div>
          </>
        )}

        {cancelStep === "done" && (
          <div className="text-center py-4">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3"><Check size={28} className="text-green-600" /></div>
            <h3 className="text-lg font-bold text-[#1E1B4B] mb-1">Booking Cancelled</h3>
            <p className="text-sm text-[#475569] mb-4">Your refund will be processed within 5-7 business days.</p>
            <div className="flex gap-2">
              <button onClick={() => { setCancelView(null); openFeedbackPopup(); }} className="flex-1 py-2.5 rounded-xl bg-[#E83E8C] text-white font-medium text-sm" data-testid="share-feedback-btn">Share Feedback</button>
              <button onClick={() => { setCancelView(null); fetchProfile(); }} className="flex-1 py-2.5 rounded-xl border border-gray-300 text-[#475569] text-sm">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const FeedbackModal = ({
  showFeedback, setShowFeedback, feedbackText, setFeedbackText,
  feedbackRating, setFeedbackRating, feedbackSent, submitFeedback, fetchProfile,
}) => {
  if (!showFeedback) return null;
  const { Star } = require("lucide-react");

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowFeedback(false)}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        {!feedbackSent ? (
          <>
            <h3 className="text-lg font-bold text-[#1E1B4B] mb-1">Help us improve</h3>
            <p className="text-sm text-[#475569] mb-4">We're sorry to see you cancel. Your feedback means the world to us.</p>
            <div className="flex items-center justify-center gap-1 mb-4">
              {[1,2,3,4,5].map(s => (
                <button key={s} onClick={() => setFeedbackRating(s)} className="p-1" data-testid={`feedback-star-${s}`}>
                  <Star size={28} fill={s <= feedbackRating ? "#F59E0B" : "none"} stroke={s <= feedbackRating ? "#F59E0B" : "#CBD5E1"} />
                </button>
              ))}
            </div>
            <textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} placeholder="Where can we improve? What could we have done better?" className="w-full border-2 border-gray-200 rounded-xl p-3 text-sm outline-none focus:border-[#E83E8C] mb-3" rows={3} data-testid="feedback-textarea" />
            <button onClick={submitFeedback} disabled={!feedbackText.trim()} className="w-full py-2.5 rounded-xl bg-[#E83E8C] text-white font-semibold text-sm disabled:opacity-50" data-testid="submit-feedback-btn">Submit Feedback</button>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3"><Check size={24} className="text-green-600" /></div>
            <h3 className="text-lg font-bold text-[#1E1B4B] mb-1">Thank you!</h3>
            <p className="text-sm text-[#475569] mb-4">Your feedback helps us serve you better.</p>
            <button onClick={() => { setShowFeedback(false); fetchProfile(); }} className="px-6 py-2 rounded-xl bg-[#E83E8C] text-white text-sm font-medium">Done</button>
          </div>
        )}
      </div>
    </div>
  );
};
